import { injectable, inject } from 'inversify';
import {
    CONTRACTS,
    WindowsServiceContract,
    DialogButton,
    FetchServiceContract,
    AuditServiceContract
} from '@/core/contracts';
import { TilesContainer, TileData } from '@/core/components/tiles/types';
import { throttle } from 'lodash';
import { ContextsState, SettingsState } from '@/store';
import config from '@/config';
import { UserActionType } from '@/dto';

const RESIZE_THROTTLE_MS = 1000 / 30; // 30 fps

@injectable()
export class TilesService implements WindowsServiceContract {
    public getLayout = throttle(
        this.getLayoutThrottled.bind(this),
        RESIZE_THROTTLE_MS
    );

    private _container?: TilesContainer;
    private _fetchService?: FetchServiceContract;
    private _auditService?: AuditServiceContract;

    /**
     * Creates instance of the service
     * @param { FetchServiceContract } fetchService The service for interacting with the backend
     */
    constructor(
        @inject(CONTRACTS.FetchService) fetchService: FetchServiceContract,
        @inject(CONTRACTS.AuditService) auditService: AuditServiceContract
    ) {
        if (!fetchService) {
            throw new Error('fetchService is not set');
        }

        this._fetchService = fetchService;
        this._auditService = auditService;
    }

    private get TileSettings() {
        return SettingsState.contextSettings.tileData;
    }

    public open(name: string, args?: any): string {
        this.checkContainer();
        const id = this._container!.open(name, args);

        if (id) {
            this.layoutChanged();
        }

        // audit event
        const contextId = ContextsState.currentContextId;
        this._auditService!.logAction(contextId, UserActionType.WindowOpen, {
            name
        });

        return id;
    }

    public update(name: string, args?: any): void {
        this.checkContainer();
        this._container!.update(name, args);
    }

    public storeCurrentTile(key: string): void {
        this.checkContainer();
        this._container!.storeCurrentTile(key);
    }

    public showModalComponent(component: Vue, args?: any): Promise<any> {
        if (!this._container) {
            return Promise.resolve();
        }
        return this._container!.showModalComponent(component, args);
    }

    public showDialog(
        message?: string,
        buttons?: DialogButton[]
    ): Promise<any> {
        if (!this._container) {
            return Promise.resolve();
        }
        return this._container!.showDialog(message, buttons);
    }

    public close(id: string): boolean {
        this.checkContainer();
        const changed = this._container!.close(id);
        if (changed) {
            this.layoutChanged();
        }
        return changed;
    }

    public closeAll(): void {
        this._container!.closeAll();
    }

    public expand(id: string): void {
        this.checkContainer();
        this._container!.expand(id);
        this.layoutChanged();
    }

    public restoreLayout(tiles: TileData[]) {
        this.checkContainer();
        if (this.saveTileState === false) {
            return;
        }
        this._container!.restoreLayout(tiles);
    }

    public setContainer(container: TilesContainer) {
        this._container = container;
    }

    public get getTileSettings() {
        return this.TileSettings;
    }

    private async layoutChanged() {
        this.checkContainer();
        const layout: TileData[] = this.getLayout();
        const contextId = ContextsState.currentContextId;
        const postedLayout: TileData[] = [];

        layout.forEach(element => {
            if (
                config.tileData.tileDataExceptions.includes(element.name) ===
                    false &&
                JSON.stringify(element).length < config.tileData.maxSizeTileData
            ) {
                postedLayout.push(element);
            }
        });

        await this._fetchService!.put(
            `/contexts/${contextId}/settings/tiledata`,
            postedLayout
        );
    }

    private get saveTileState(): boolean {
        return SettingsState.contextSettings.global.saveTileState === undefined
            ? true
            : SettingsState.contextSettings.global.saveTileState;
    }

    private checkContainer(): void {
        if (!this._container) {
            throw new Error('No tile container found.');
        }
    }

    private getLayoutThrottled(): TileData[] {
        this.checkContainer();
        return this._container!.getLayout();
    }
}
